import React, { useState, useContext } from 'react';

import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import ButtonPrimary from '../ButtonPrimary';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { GradientContext, BackgroundContext } from '../../contexts/PageContexts';
import classNames from 'classnames';
import { trackShuffle, trackReadMore } from '../../utils/dataLayer';
import PhotoCred from '../PhotoCred';
import styles from './Shuffle.module.scss';

const Shuffle = ({ titleTop, titleBottom, text, color = 'black', item = {}, href = {} }) => {
    const [ref, isVisible] = useIntersectionObserver();
    const [shuffleItem, setShuffleItem] = useState(item);
    const [isFetching, setIsFetching] = useState(false);
    const gradient = useContext(GradientContext);
    const background = useContext(BackgroundContext) || false;

    const { whiteBackground } = background;
    const getNewItem = async () => {
        setIsFetching(true);
        trackShuffle();

        const resp = await fetch(
            `${process.env.NEXT_PUBLIC_WAGTAIL_API_URL}/v1/get_shuffle_item/?not=${shuffleItem?.index}`
        );
        const data = await resp.json();

        setTimeout(() => {
            setShuffleItem(data.item);
            setIsFetching(false);
        }, 300);
    };

    const { t } = useTranslation();

    const rootClasses = classNames(styles['Root'], styles[gradient], {
        [styles['White']]: color !== 'black' || whiteBackground,
    });

    const titleClasses = classNames(styles['TitleContainer'], 'grid', {
        [styles['Visible']]: isVisible,
        [styles['White']]: color !== 'black',
    });

    const textClasses = classNames(styles['TextContainer'], 'grid', {
        [styles['Visible']]: isVisible,
        [styles['White']]: color !== 'black',
    });

    const buttonDesktopClasses = classNames(styles['ButtonDesktop'], 'grid', {
        [styles['Visible']]: isVisible,
    });

    const buttonMobileClasses = classNames(styles['ButtonMobile'], 'grid', {
        [styles['Visible']]: isVisible,
    });

    const itemClasses = classNames(styles['Item'], {
        [styles['Visible']]: isVisible,
        [styles['White']]: color !== 'black',
        [styles['Fetching']]: isFetching,
    });

    const isExternal =
        shuffleItem?.href?.href?.indexOf('http://') !== -1 || shuffleItem?.href?.href?.indexOf('https://') !== -1;

    const handleTrackReadMore = (e) => {
        e.preventDefault();

        trackReadMore(shuffleItem?.title, 'Shuffle');

        setTimeout(() => {
            window.location.href = shuffleItem?.href;
        }, 200);
    };

    return (
        <section className={rootClasses} ref={ref}>
            <div className={titleClasses}>
                <span className={styles['TitleTop']}>{titleTop}</span>
                <span className={styles['TitleBottom']}>{titleBottom}</span>
            </div>
            <div className={textClasses}>
                <p className={styles['Text']}>{text}</p>
            </div>
            <div className={`${styles['ItemWrapper']} grid`}>
                <div className={itemClasses}>
                    <div className={styles['ImageContainer']}>
                        <Image
                            className={styles['Image']}
                            src={shuffleItem?.image?.url}
                            alt={shuffleItem?.image?.alt || ''}
                            title={shuffleItem?.image?.title || ''}
                            fill
                        />
                        <PhotoCred credit={shuffleItem?.image?.credit} />
                    </div>
                    <div className={`${styles['Content']} ${color !== 'black' ? 'white-glass' : 'black-glass'}`}>
                        <span className={styles['Index']}>{t('Shuffle.index', { index: shuffleItem?.index })}</span>
                        <h5 className={styles['CardTitle']}>{shuffleItem?.title}</h5>
                        <p className={styles['CardText']}>{shuffleItem?.text}</p>
                        {!!shuffleItem?.href && (
                            <Link href={shuffleItem?.href} legacyBehavior>
                                <a onClick={handleTrackReadMore} className={styles['Link']}>
                                    <Image
                                        width={isExternal ? 18 : 20}
                                        height={isExternal ? 18 : 20}
                                        src={
                                            isExternal
                                                ? '/img/icon-external-white.svg'
                                                : '/img/icon-arrow-right-white.svg'
                                        }
                                        alt=""
                                        className={!isExternal ? styles['Arrow'] : ''}
                                    />
                                    {t('Generic.readMore')}
                                    <span className="sr-only">{shuffleItem?.title}</span>
                                </a>
                            </Link>
                        )}
                    </div>
                    <div className={buttonDesktopClasses}>
                        <ButtonPrimary icon={'icon-shuffle-white'} onClick={getNewItem} modifiers={['ShuffleDesktop']}>
                            {t('Shuffle.newGuide', styles[gradient])}
                        </ButtonPrimary>
                    </div>
                </div>
            </div>
            <div className={buttonMobileClasses}>
                <ButtonPrimary
                    href={href.href}
                    icon={'icon-shuffle-white'}
                    onClick={getNewItem}
                    modifiers={['ShuffleMobile', styles[gradient]]}>
                    {!!href.text ? href.text : t('Shuffle.newGuide')}
                </ButtonPrimary>
            </div>
        </section>
    );
};

Shuffle.propTypes = {};

export default Shuffle;
